"use client";

import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Clock3, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

import { HeaderDeviceSelect } from "@/components/layout/header-device-select";
import { Alert, Button, Card, EmptyState, EnterprisePageShell, SegmentedControl, StatusIndicator } from "@/components/ui-system";
import { getDeviceById, getDevices } from "@/lib/api/endpoints/devices";
import { getLiveTelemetry, getLiveTelemetryAggregates } from "@/lib/api/endpoints/telemetry";
import type { JsonObject } from "@/lib/api/types/json";
import type { DeviceChannelDto } from "@/lib/api/types";
import type { GraphType } from "@/modules/core/datalogger-create/schema/datalogger.schema";
import {
  deviceActivityModelFromApi,
  formatDeviceOperationalStatusLabel,
  operationalStatusToIndicatorVariant,
} from "@/lib/devices/device-state-model";
import { cn } from "@/lib/utils/cn";
import { useAuthStore } from "@/modules/auth/store";
import { readDeviceDisableState } from "@/lib/devices/device-disable-state";
import { LiveChannelTile } from "@/modules/live/components/live-channel-tile";
import { LiveWindRoseTile } from "@/modules/live/components/live-wind-rose-tile";
import { entitlementBlockedMessage } from "@/modules/telemetry/entitlement-ui";
import { formatBooleanState, formatRelativeTime, formatTelemetryValue, getLatestTelemetryRow } from "@/modules/telemetry/page-utils";
import { getChannelThresholds } from "@/modules/telemetry/thresholds";
import { useReportingDeviceStore } from "@/store/reporting-device";
import { useWorkspaceChromeStore } from "@/store/workspace-chrome";

const LIVE_WINDOWS = [
  { label: "1h", hours: 1 },
  { label: "6h", hours: 6 },
  { label: "24h", hours: 24 },
] as const;

const POLL_INTERVAL_MS = 5_000;

type WindRoseConfig = {
  enabled: boolean;
  speedChannelKey: string;
  directionChannelKey: string;
};

function readChannelGraphTypes(device: { metadata?: JsonObject | null; channels?: DeviceChannelDto[] } | null | undefined): Record<string, GraphType> {
  const metadata = device?.metadata;
  const channelMeta = Array.isArray(metadata?.channels) ? metadata.channels : [];
  const byKey: Record<string, GraphType> = {};

  channelMeta.forEach((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
      return;
    }
    const key = typeof entry.key === "string" ? entry.key : "";
    const graphType = entry.graphType;
    if (!key) {
      return;
    }
    if (
      graphType === "line" ||
      graphType === "area" ||
      graphType === "bar" ||
      graphType === "scatter" ||
      graphType === "gauge" ||
      graphType === "step" ||
      graphType === "histogram" ||
      graphType === "stacked_bar" ||
      graphType === "donut" ||
      graphType === "heatmap" ||
      graphType === "radar" ||
      graphType === "candlestick" ||
      graphType === "ohlc" ||
      graphType === "gantt" ||
      graphType === "table"
    ) {
      byKey[key] = graphType;
    }
  });

  return byKey;
}

function bucketSecondsForWindow(hours: number): number {
  if (hours <= 1) return 30;
  if (hours <= 6) return 120;
  return 300;
}

function readWindRoseConfig(device: { metadata?: JsonObject | null } | null | undefined): WindRoseConfig | null {
  const metadata = device?.metadata;
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) {
    return null;
  }
  const visualizations = metadata.visualizations;
  if (!visualizations || typeof visualizations !== "object" || Array.isArray(visualizations)) {
    return null;
  }
  const visualizationsObject = visualizations as JsonObject;
  const windRose = visualizationsObject.wind_rose;
  if (!windRose || typeof windRose !== "object" || Array.isArray(windRose)) {
    return null;
  }
  const windRoseObject = windRose as JsonObject;
  return {
    enabled: Boolean(windRoseObject.enabled),
    speedChannelKey: typeof windRoseObject.speed_channel_key === "string" ? windRoseObject.speed_channel_key : "",
    directionChannelKey: typeof windRoseObject.direction_channel_key === "string" ? windRoseObject.direction_channel_key : "",
  };
}

export function LiveMonitoringPage() {
  const tenantId = useAuthStore((s) => s.tenantId);
  const user = useAuthStore((s) => s.user);
  const setWorkspaceChrome = useWorkspaceChromeStore((s) => s.setWorkspaceChrome);
  const selectedDeviceId = useReportingDeviceStore((s) => s.selectedDeviceId);
  const setSelectedDeviceId = useReportingDeviceStore((s) => s.setSelectedDeviceId);
  const [windowHours, setWindowHours] = useState<(typeof LIVE_WINDOWS)[number]["hours"]>(1);
  const [isPollingPaused, setIsPollingPaused] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const allWorkspaces = user?.role === "super_admin" || user?.accountType === "platform";

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  const devicesQuery = useQuery({
    queryKey: ["live", "devices", allWorkspaces ? "all" : tenantId],
    queryFn: () => getDevices(allWorkspaces ? null : tenantId ?? null, allWorkspaces ? "all" : null, "live"),
    enabled: allWorkspaces || Boolean(tenantId),
    staleTime: 30_000,
  });

  const devices = useMemo(() => devicesQuery.data ?? [], [devicesQuery.data]);

  useLayoutEffect(() => {
    setWorkspaceChrome({
      headerAction: (
        <HeaderDeviceSelect
          devices={devices}
          value={selectedDeviceId}
          onChange={setSelectedDeviceId}
          loading={devicesQuery.isLoading}
          disabled={!allWorkspaces && !tenantId}
        />
      ),
    });
    return () => setWorkspaceChrome({ headerAction: null });
  }, [allWorkspaces, devices, devicesQuery.isLoading, selectedDeviceId, setWorkspaceChrome, tenantId]);

  useEffect(() => {
    if (devicesQuery.isLoading) {
      return;
    }
    if (devices.length === 0) {
      if (selectedDeviceId) {
        setSelectedDeviceId("");
      }
      return;
    }
    const exists = devices.some((device) => device.id === selectedDeviceId);
    if (!exists) {
      setSelectedDeviceId(devices[0]?.id ?? "");
    }
  }, [devices, devicesQuery.isLoading, selectedDeviceId, setSelectedDeviceId]);

  const selectedDeviceSummary = useMemo(
    () => devices.find((device) => device.id === selectedDeviceId) ?? null,
    [devices, selectedDeviceId],
  );

  const deviceDetailQuery = useQuery({
    queryKey: ["live", "device", selectedDeviceId, allWorkspaces ? "all" : "tenant"],
    queryFn: () => getDeviceById(selectedDeviceId, allWorkspaces ? "all" : null, "live"),
    enabled: Boolean(selectedDeviceId),
    staleTime: 30_000,
  });

  const selectedDevice = deviceDetailQuery.data ?? selectedDeviceSummary;
  const disableState = useMemo(() => readDeviceDisableState(selectedDevice), [selectedDevice]);
  const disableCutoff = disableState?.disabledAt ?? null;
  const disableCutoffKey = disableCutoff?.toISOString() ?? "none";

  const numericChannels = useMemo(
    () => (selectedDevice?.channels ?? []).filter((channel) => channel.data_type === "number"),
    [selectedDevice],
  );
  const discreteChannels = useMemo(
    () => (selectedDevice?.channels ?? []).filter((channel) => channel.data_type !== "number"),
    [selectedDevice],
  );

  const metricsParam = useMemo(
    () => numericChannels.map((channel) => channel.key).join(",") || undefined,
    [numericChannels],
  );

  const bucketSeconds = bucketSecondsForWindow(windowHours);

  const aggregatesQuery = useQuery({
    queryKey: ["live", "aggregates", selectedDeviceId, windowHours, metricsParam, bucketSeconds, disableCutoffKey],
    queryFn: async () => {
      const now = new Date();
      const effectiveEnd = disableCutoff && disableCutoff.getTime() < now.getTime() ? disableCutoff : now;
      const start = new Date(effectiveEnd.getTime() - windowHours * 60 * 60 * 1000);
      return getLiveTelemetryAggregates({
        deviceId: selectedDeviceId,
        start,
        end: effectiveEnd,
        bucketSeconds,
        maxPoints: 720,
        metrics: metricsParam,
        workspaceScope: allWorkspaces ? "all" : null,
      });
    },
    enabled: Boolean(selectedDeviceId && metricsParam),
    staleTime: 0,
    refetchInterval: isPollingPaused ? false : POLL_INTERVAL_MS,
  });

  const latestQuery = useQuery({
    queryKey: ["live", "latest", selectedDeviceId, windowHours, allWorkspaces ? "all" : "tenant", disableCutoffKey],
    queryFn: async () => {
      const now = new Date();
      const effectiveEnd = disableCutoff && disableCutoff.getTime() < now.getTime() ? disableCutoff : now;
      const start = new Date(effectiveEnd.getTime() - windowHours * 60 * 60 * 1000);
      return getLiveTelemetry(selectedDeviceId, { start, end: effectiveEnd }, allWorkspaces ? "all" : null);
    },
    enabled: Boolean(selectedDeviceId),
    staleTime: 0,
    refetchInterval: isPollingPaused ? false : POLL_INTERVAL_MS,
  });

  const latestRow = useMemo(() => getLatestTelemetryRow(latestQuery.data ?? []), [latestQuery.data]);
  const livePoints = aggregatesQuery.data?.points ?? [];
  const lastAggregatePoint = livePoints[livePoints.length - 1];
  const displayUpdatedAt = lastAggregatePoint?.t ?? latestRow?.time ?? null;
  const liveModel = selectedDevice ? deviceActivityModelFromApi(selectedDevice) : null;
  const deviceStatusVariant = liveModel ? operationalStatusToIndicatorVariant(liveModel.status) : "neutral";
  const selectedDeviceLabel = selectedDevice?.name || selectedDevice?.device_uid || "Select a device";
  const pageTitle = selectedDevice ? `Live - ${selectedDeviceLabel}` : "Live";
  const channelGraphTypes = useMemo(() => readChannelGraphTypes(selectedDevice), [selectedDevice]);
  const windRoseConfig = useMemo(() => readWindRoseConfig(selectedDevice), [selectedDevice]);
  const windRoseChannels = useMemo(() => {
    if (!windRoseConfig?.enabled) {
      return null;
    }
    const speedChannel = numericChannels.find((channel) => channel.key === windRoseConfig.speedChannelKey) ?? null;
    const directionChannel = numericChannels.find((channel) => channel.key === windRoseConfig.directionChannelKey) ?? null;
    return speedChannel && directionChannel
      ? { speedChannel, directionChannel }
      : null;
  }, [numericChannels, windRoseConfig]);
  const blockedMessage = entitlementBlockedMessage(aggregatesQuery.error, latestQuery.error);

  const sessionMetrics = [
    {
      title: "Numeric channels",
      value: String(numericChannels.length),
      hint: selectedDevice ? `${selectedDevice.channels?.length ?? 0} total configured channels` : "Waiting for device selection",
    },
    {
      title: "Window points",
      value: String(livePoints.length),
      hint: `${bucketSeconds} second buckets`,
    },
    {
      title: "Polling",
      value: `${POLL_INTERVAL_MS / 1000}s`,
      hint: "Live refresh interval",
    },
  ];

  return (
    <EnterprisePageShell
      layout="dashboard"
      title={pageTitle}
      titleHref={selectedDevice ? `/devices/${selectedDevice.id}` : undefined}
      hideHeaderSubtitle
      titleAdornment={
        <span
          className={cn(
            "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-[0.12em]",
            liveModel?.status === "ONLINE" && "border-success/35 bg-success/12 text-success",
            liveModel?.status === "OFFLINE" && "border-border/70 bg-muted-bg/55 text-muted",
            liveModel?.status === "STALE" && "border-warning/35 bg-warning/12 text-warning",
            liveModel?.status === "ERROR" && "border-error/35 bg-error/12 text-error",
            liveModel?.status === "MAINTENANCE" && "border-accent/35 bg-accent/12 text-accent",
            liveModel?.status === "VISUALIZATION_DISABLED" && "border-warning/35 bg-warning/12 text-warning",
            liveModel?.status === "INGESTION_DISABLED" && "border-error/35 bg-error/12 text-error",
            !liveModel && "border-border/70 bg-muted-bg/55 text-muted",
          )}
          title={liveModel?.delayLabel}
        >
          <StatusIndicator
            variant={deviceStatusVariant}
            size="sm"
            pulse={liveModel?.status === "ONLINE"}
            showRing={liveModel?.status === "STALE"}
          />
          {formatDeviceOperationalStatusLabel(liveModel?.status)}
        </span>
      }
    >
      <div className="space-y-ds-5">
        {devicesQuery.isError ? (
          <Alert
            tone="error"
            title="Live devices unavailable"
            description="Could not load devices for the Live page."
            action={<Button size="sm" variant="secondary" onClick={() => void devicesQuery.refetch()}>Retry</Button>}
          />
        ) : null}

        {devices.length === 0 ? (
          <EmptyState title="No devices are available yet" description="Add a workspace logger, then return to Live monitoring." />
        ) : (
          <>
            <Card className="py-ds-3">
              <div className="flex flex-col gap-ds-4 xl:flex-row xl:items-center xl:justify-between">
                <div className="grid min-w-0 flex-1 grid-cols-3 divide-x divide-card-border sm:max-w-[31rem]">
                  {sessionMetrics.map((metric) => (
                    <div key={metric.title} className="min-w-0 px-ds-3 first:pl-0 sm:px-ds-4">
                      <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">{metric.title}</p>
                      <p className="mt-1 text-title font-semibold tabular-nums text-foreground">{metric.value}</p>
                      <p className="mt-1 truncate text-caption text-muted" title={metric.hint}>{metric.hint}</p>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap items-center justify-start gap-ds-2 text-caption text-muted xl:justify-end">
                  <SegmentedControl
                    value={String(windowHours)}
                    ariaLabel="Live window"
                    options={LIVE_WINDOWS.map((option) => ({ value: String(option.hours), label: option.label }))}
                    onChange={(value) => setWindowHours(Number(value) as (typeof LIVE_WINDOWS)[number]["hours"])}
                  />
                  <div className="dashboard-meta-item" title={isPollingPaused ? "Live polling paused" : "Live polling active"}>
                    <RefreshCw className={cn("h-3.5 w-3.5", aggregatesQuery.isFetching || latestQuery.isFetching ? "animate-spin text-accent" : "")} aria-hidden />
                    {isPollingPaused ? "Paused" : "Live"}
                  </div>
                  <div className="dashboard-meta-item">
                    {liveModel?.delayLabel ?? "Waiting for telemetry"}
                  </div>
                  <div className="dashboard-meta-item">
                    <Clock3 className="h-3.5 w-3.5" aria-hidden />
                    {displayUpdatedAt ? formatRelativeTime(displayUpdatedAt, nowMs) : "Waiting for payload"}
                  </div>
                    <Button size="sm" variant="secondary" onClick={() => setIsPollingPaused((paused) => !paused)}>
                      {isPollingPaused ? "Resume" : "Pause"}
                    </Button>
                    <Link to={selectedDevice ? `/explorer?device=${selectedDevice.id}` : "/explorer"} className="btn btn-ghost btn-sm !no-underline">
                      Explore history
                      <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                    </Link>
                </div>
              </div>
            </Card>

            {disableState ? (
              <Alert tone={disableState.mode === "ingestion" ? "error" : "warning"} title={disableState.mode === "ingestion" ? "Ingestion disabled" : "Visualization disabled"} description={disableState.mode === "ingestion" ? "No new telemetry, live data, or alerts will be accepted." : "Last-known data remains visible up to the disable timestamp while ingestion continues."} />
            ) : null}

            {blockedMessage ? (
              <Alert tone="warning" title="Live access limited" description={blockedMessage} />
            ) : deviceDetailQuery.isError || aggregatesQuery.isError || latestQuery.isError ? (
              <Alert
                tone="error"
                title="Live telemetry unavailable"
                description="Check the backend and selected device channels, then retry the live request."
                action={(
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      void deviceDetailQuery.refetch();
                      void aggregatesQuery.refetch();
                      void latestQuery.refetch();
                    }}
                  >
                    Retry
                  </Button>
                )}
              />
            ) : null}

            {numericChannels.length === 0 ? (
              <EmptyState title="No numeric channels to visualize" description="Add numeric channels in device configuration to unlock live charts." />
            ) : (
              <div className="space-y-ds-4">
                <div className={cn("grid gap-ds-4", numericChannels.length > 1 && "xl:grid-cols-2")}>
                  {numericChannels.map((channel) => (
                    <LiveChannelTile
                      key={channel.id}
                      deviceId={selectedDeviceId}
                      channel={channel}
                      aggregatePoints={livePoints}
                      rawRows={latestQuery.data ?? []}
                      dataRevision={aggregatesQuery.dataUpdatedAt}
                      latestValue={readLatestChannelValue(channel, latestRow?.data, lastAggregatePoint?.values)}
                      updatedAt={displayUpdatedAt}
                      thresholds={getChannelThresholds(selectedDevice, channel)}
                      graphType={channelGraphTypes[channel.key] ?? "line"}
                    />
                  ))}
                </div>

                {discreteChannels.length > 0 ? (
                  <div className="space-y-ds-3">
                    <div>
                      <p className="text-title font-semibold text-foreground">State and text channels</p>
                      <p className="text-caption text-muted">Latest boolean and string readings stay visible here without forcing them into numeric charts.</p>
                    </div>
                    <div className={cn("grid gap-ds-4", discreteChannels.length > 1 && "xl:grid-cols-2")}>
                      {discreteChannels.map((channel) => {
                        const latestValue = readLatestChannelValue(channel, latestRow?.data, lastAggregatePoint?.values);
                        const displayValue =
                          channel.data_type === "boolean"
                            ? formatBooleanState(latestValue)
                            : formatTelemetryValue(latestValue);
                        return (
                          <Card key={channel.id} className="space-y-ds-4 border-border/60 bg-muted-bg/15 p-ds-4">
                            <div className="flex items-start justify-between gap-ds-3">
                              <div>
                                <p className="text-title font-semibold text-foreground">{channel.label || channel.key}</p>
                                <p className="text-micro font-semibold uppercase tracking-[0.12em] text-muted">
                                  {channel.data_type === "boolean" ? "Boolean state" : "String channel"}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-title font-semibold text-foreground">{displayValue}</p>
                                <p className="text-caption text-muted">{displayUpdatedAt ? formatRelativeTime(displayUpdatedAt, nowMs) : "Waiting for payload"}</p>
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                ) : null}

                {windRoseConfig?.enabled && !windRoseChannels ? (
                  <Alert tone="warning" title="Wind rose configuration incomplete" description="The configured speed or direction channel is missing from the numeric channel list." />
                ) : null}

                {windRoseChannels ? (
                  <LiveWindRoseTile
                    speedChannel={windRoseChannels.speedChannel}
                    directionChannel={windRoseChannels.directionChannel}
                    rawRows={latestQuery.data ?? []}
                    aggregatePoints={livePoints}
                    updatedAt={displayUpdatedAt}
                  />
                ) : null}

              </div>
            )}
          </>
        )}
      </div>
    </EnterprisePageShell>
  );
}

function readLatestChannelValue(
  channel: DeviceChannelDto,
  latestPayload: Record<string, unknown> | null | undefined,
  fallbackValues: Record<string, number | null> | undefined,
) {
  if (latestPayload && channel.key in latestPayload) {
    return latestPayload[channel.key];
  }
  return fallbackValues?.[channel.key] ?? null;
}
